import { Box, Typography, TextField, Button, Grid, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import COLORS from '../../../../constants/colors';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faEnvelope, faEye, faEyeSlash, faPhone, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import Header from './Header';
import SnackbarAlert from '../Reusbale/SnackbarAlert';
import baseURL from '../config';

const Register = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        physicalAddress: '',
        password: '',
        confirmPassword: '' // Add confirm password field
    });

    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
    
        // Check if all fields are filled
        if (!formData.name || !formData.email || !formData.phone || !formData.physicalAddress || !formData.password || !formData.confirmPassword) {
            setSnackbar({ open: true, message: 'All fields are required!', severity: 'error' });
            setLoading(false);
            return;
        }
    
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setSnackbar({ open: true, message: 'Please enter a valid email address.', severity: 'error' });
            setLoading(false);
            return;
        }
    
        // Validate phone number (only numbers, 10 digits)
        const phoneRegex = /^[0-9]{10}$/; // Adjust this regex based on your country’s phone number format
        if (!phoneRegex.test(formData.phone)) {
            setSnackbar({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
            setLoading(false);
            return;
        }

        // Password strength check
        const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(formData.password)) {
            setSnackbar({
                open: true,
                message: 'Password must be at least 8 characters long, contain at least one letter, one number, and one special character.',
                severity: 'error'
            });
            setLoading(false);
            return;
        }

        // Check if password and confirm password match
        if (formData.password !== formData.confirmPassword) {
            setSnackbar({ open: true, message: 'Passwords do not match!', severity: 'error' });
            setLoading(false);
            return;
        }
    
        try {
            const response = await fetch(`${baseURL}/auth/user/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
    
            const data = await response.json();
    
            if (!response.ok) {
                throw new Error(data.message || 'Something went wrong');
            }
    
            setSnackbar({ open: true, message: 'Registration successful!', severity: 'success' });
            
            // Clear the form after successful registration
            setFormData({
                name: '',
                email: '',
                phone: '',
                physicalAddress: '',
                password: '',
                confirmPassword: ''
            });

        } catch (error) {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box>
            <Header />
            <Box sx={{ py: 3, px: 3, maxWidth: 600, margin: '0 auto', mt: 8, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color={COLORS.primary} mb={2}>
                    Create an Account
                </Typography>
                <Typography variant="body1" color={COLORS.text} mb={4}>
                    Sign up and start exploring today.
                </Typography>

                {/* Registration Form */}
                <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField name="name" label="Full Name" variant="outlined" fullWidth value={formData.name} onChange={handleChange} sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} InputLabelProps={{ style: { color: COLORS.primary } }} InputProps={{ style: { color: COLORS.text }, startAdornment: (<InputAdornment position="start"><FontAwesomeIcon icon={faUser} style={{ color: COLORS.primary }} /></InputAdornment>) }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField name="email" label="Email Address" variant="outlined" fullWidth value={formData.email} onChange={handleChange} sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} InputLabelProps={{ style: { color: COLORS.primary } }} InputProps={{ style: { color: COLORS.text }, startAdornment: (<InputAdornment position="start"><FontAwesomeIcon icon={faEnvelope} style={{ color: COLORS.primary }} /></InputAdornment>) }} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                name="phone" 
                                label="Phone Number" 
                                variant="outlined" 
                                fullWidth 
                                value={formData.phone} 
                                onChange={handleChange} 
                                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} 
                                InputLabelProps={{ style: { color: COLORS.primary } }} 
                                InputProps={{ 
                                    style: { color: COLORS.text }, 
                                    startAdornment: (<InputAdornment position="start"><FontAwesomeIcon icon={faPhone} style={{ color: COLORS.primary }} /></InputAdornment>), 
                                    inputProps: { pattern: "[0-9]*", inputMode: "numeric" }
                                }} 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField name="physicalAddress" label="Physical Address" variant="outlined" fullWidth value={formData.physicalAddress} onChange={handleChange} sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} InputLabelProps={{ style: { color: COLORS.primary } }} InputProps={{ style: { color: COLORS.text }, startAdornment: (<InputAdornment position="start"><FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: COLORS.primary }} /></InputAdornment>) }} />
                        </Grid>
                    </Grid>

                    <TextField 
                        name="password" 
                        label="Password" 
                        variant="outlined" 
                        fullWidth 
                        type={showPassword ? "text" : "password"} 
                        value={formData.password} 
                        onChange={handleChange} 
                        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} 
                        InputLabelProps={{ style: { color: COLORS.primary } }} 
                        InputProps={{ 
                            style: { color: COLORS.text }, 
                            startAdornment: (<InputAdornment position="start"><FontAwesomeIcon icon={faLock} style={{ color: COLORS.primary }} /></InputAdornment>), 
                            endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end"><FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} style={{ fontSize: '16px' }} /></IconButton></InputAdornment>) 
                        }} 
                    />

                    {/* Confirm Password Field */}
                    <TextField 
                        name="confirmPassword" 
                        label="Confirm Password" 
                        variant="outlined" 
                        fullWidth 
                        type={showConfirmPassword ? "text" : "password"} 
                        value={formData.confirmPassword} 
                        onChange={handleChange} 
                        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} 
                        InputLabelProps={{ style: { color: COLORS.primary } }} 
                        InputProps={{ 
                            style: { color: COLORS.text }, 
                            startAdornment: (<InputAdornment position="start"><FontAwesomeIcon icon={faLock} style={{ color: COLORS.primary }} /></InputAdornment>), 
                            endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end"><FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} style={{ fontSize: '16px' }} /></IconButton></InputAdornment>) 
                        }} 
                    />

                    <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ backgroundColor: COLORS.primary, color: COLORS.white, borderRadius: '10px', py: 1.5, fontSize: '0.9rem', textTransform: 'none', '&:hover': { backgroundColor: COLORS.secondary } }}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Register'}
                    </Button>
                </Box>

                <Box mt={2}>
                    <Typography variant="body2" color={COLORS.text}>
                        Already have an account? <span style={{ color: COLORS.primary, cursor: 'pointer', fontWeight: 'bold' }}>Login</span>
                    </Typography>
                </Box>
            </Box>

            {/* Snackbar for Messages */}
            <SnackbarAlert open={snackbar.open} message={snackbar.message} severity={snackbar.severity} handleClose={handleCloseSnackbar} />
        </Box>
    );
};

export default Register;
